import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ReitMetadataResponse,
  ReitOverviewService,
  ReitService,
} from '@kolytics/shared-api';
import {
  autoDestroy,
  followDestroy,
  formatPercentage,
} from '@kolytics/shared-functions';
import { colorPallete, GraphId } from '@kolytics/types/graph';
import { EChartsOption } from 'echarts';
import { XAXisOption } from 'echarts/types/dist/shared';
import * as moment from 'moment';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import {
  IReit,
  IReitDataResponse,
  TotalReturnGraphItem,
  TotalReturnGraphResponse,
} from '../../../interfaces';
import { getDynamicRange } from '../../../utils';
import { GraphComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/graph/graph.component';
import { NgClass } from '@angular/common';
import { SectionComponent } from '../../../../../../../../../libs/shared-components/src/lib/components/section/section.component';

@Component({
  selector: 'klt-overview-performance',
  templateUrl: './overview-performance.component.html',
  styleUrls: ['./overview-performance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [SectionComponent, NgClass, GraphComponent],
})
@autoDestroy()
export class OverviewPerformanceComponent implements OnInit {
  @Input() title!: string;
  @Input() reitId!: string;

  @Input() reitData!: IReitDataResponse;
  @Input() chartData!: TotalReturnGraphResponse;

  @Input() metadata!: ReitMetadataResponse;

  @Input() showTimeSelect = true;
  @Input() height =
    'calc(var(--size--spacing-x67-5) + var(--size--spacing-x2-5))';

  public times = ['1M', 'YTD', '1Y', '5Y', '10Y'];
  @Input() public options: EChartsOption | undefined = {
    grid: {
      backgroundColor: '#ffffff',
      show: true,
      borderColor: '#ffffff',
      borderWidth: 0,
      height: '86%',
      width: '85%',
      top: '6',
      left: '70',
    },
    xAxis: {
      type: 'category',
      data: [],
      boundaryGap: false,
      axisLine: {
        show: false,
      },
      axisLabel: {
        margin: 22,
      },
      axisTick: {
        show: false,
      },
      axisPointer: {
        show: true,
        type: 'none',
        label: {
          show: false,
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: any, index: number) => {
          return formatPercentage(value, 0, this.metadata.culture);
        },
        margin: 20,
      },
      axisLine: {
        show: false,
      },
      splitLine: {
        show: true,
      },
      // min: (value) => {
      //   return Math.floor(value.min);
      // },
      // max: (value) => {
      //   return Math.ceil(value.max);
      // },
    },
    series: [],
    tooltip: {
      trigger: 'item',
      padding: 0,
      position: 'top',
      displayMode: 'single',
      confine: true,
      formatter: (params: any) => {
        if (params.componentType && params.componentType === 'series') {
          return `<div style="background: ${
            params.color
          }; color: var(--color--text-light); padding: var(--size--spacing-x2) var(--size--spacing-x4);">${params.seriesName.toUpperCase()}-${
            params.name
          }: (${formatPercentage(
            params.value,
            1,
            this.metadata.culture,
            1,
          )})</div>`;
        }
        return '';
      },
    },
  };

  public series: any[] = [];
  public titles: { name: string; color: string; disabled: boolean }[] = [];
  currentType = '';
  totalReturnGraphData: TotalReturnGraphResponse =
    {} as TotalReturnGraphResponse;
  loading: boolean = true;

  constructor(
    protected readonly cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private reitService: ReitService,
    private reitOverviewService: ReitOverviewService,
  ) {}

  public ngOnInit(): void {
    if (!this.reitId && !this.reitData) {
      this.activatedRoute.parent?.params
        .pipe(
          filter((params) => !!params),
          tap((data) => {
            this.reitId = data.id;
          }),
          switchMap(({ id }) => this.reitService.apiReitDataReitIdGet(id)),
          map((response: IReitDataResponse) => response.reits.reit),
          followDestroy(this),
        )
        .subscribe((reit: IReit) => {
          this.setTitles(reit.name, reit.sector.name);
          this.currentType = this.times[0];
          this.getTotalReturnGraph();
        });
    } else {
      this.setTitles(
        this.reitData.reits.reit.name,
        this.reitData.reits.reit.sector.name,
      );
      this.currentType = this.times[0];
      this.getTotalReturnGraph();
    }
  }

  getTotalReturnGraph() {
    if (this.chartData) {
      this.totalReturnGraphData = this.chartData;
      this.setXAxis(this.totalReturnGraphData);
      this.setSeries(this.totalReturnGraphData);
      this.runFilter(this.titles);
      this.loading = false;
    } else {
      this.reitOverviewService
        .apiReitReitIdOverviewTotalReturnGraphTypeGet(
          Number(this.reitId),
          this.currentType,
        )
        .pipe(filter((x) => x.graphData.length > 0 && this.titles.length === 2))
        .subscribe((data) => {
          this.totalReturnGraphData = data;
          this.setXAxis(this.totalReturnGraphData);
          this.setSeries(this.totalReturnGraphData);
          this.runFilter(this.titles);
          this.loading = false;
        });
    }
  }

  runFilter(data: { name: string; color: string; disabled: boolean }[]): void {
    const filter = data
      .filter((e) => !e.disabled)
      .map((e) => e.name.toLowerCase());

    // this.setYAxis(this.totalReturnGraphData, filter);

    const series = this.series.filter((e) =>
      filter.includes(e.name.toLowerCase()),
    );
    (this.options as EChartsOption).series = [...(series as any)];
    const temp = Object.assign({}, this.options);
    this.options = undefined;
    setTimeout(() => {
      this.options = temp;
      this.cdr.detectChanges();
    }, 0);
  }

  setXAxis(data: TotalReturnGraphResponse) {
    ((this.options as EChartsOption).xAxis as any).data = data.graphData.map(
      (x) => moment(x.point).format('DD MMM YY'),
    );
  }

  setYAxis(data: TotalReturnGraphResponse, filterNames: string[]) {
    const max = data.graphData.reduce(
      (m, el) => Math.max(m, ...this.getValueByFilterName(filterNames, el)),
      0,
    );
    const min = data.graphData.reduce(
      (m, el) => Math.min(m, ...this.getValueByFilterName(filterNames, el)),
      max,
    );

    const [, , , options] = getDynamicRange(
      min,
      max,
      undefined,
      this.options as EChartsOption,
    );
    this.options = options;
  }

  getValueByFilterName(filterNames: string[], item: TotalReturnGraphItem) {
    const yValues = [];
    if (filterNames.includes(this.titles[0].name.toLocaleLowerCase())) {
      yValues.push(item.value);
    }
    if (filterNames.includes(this.titles[1].name.toLocaleLowerCase())) {
      yValues.push(item.sectorIndexValue);
    }

    return yValues;
  }

  setSeries(data: TotalReturnGraphResponse) {
    this.series = [...this.getSeries(data)];
  }

  getSeries(data: TotalReturnGraphResponse) {
    return [
      {
        data: data.graphData.map((x) => x.cumulativeReturn),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.OverviewTotalReturnPerformance][0],
        name: this.titles[0].name,
      },
      {
        data: data.graphData.map((x) => x.sectorIndexValue),
        type: 'line',
        smooth: true,
        showSymbol: false,
        color: colorPallete[GraphId.OverviewTotalReturnPerformance][1],
        name: this.titles[1].name,
      },
    ];
  }

  setTitles(name: string, sectorName: string) {
    this.titles.push(
      {
        name: name,
        color: colorPallete[GraphId.OverviewTotalReturnPerformance][0],
        disabled: false,
      },
      {
        name: sectorName,
        color: colorPallete[GraphId.OverviewTotalReturnPerformance][1],
        disabled: false,
      },
    );
  }

  filterPerformance(type: string) {
    this.currentType = type;
    this.getTotalReturnGraph();
  }
}
